import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import classes from "../../Assets/Styles/Demo.module.css";
import GradientCarousel from "./GradientCarousel";
import EmotionWheel from "./EmotionWheel";
import TextInputArea from "./TextInputArea";
import { GENERATE_TTS_AUDIO, GENERATE_ENHANCED_TTS_AUDIO, ATTRIBUTION } from '../../ApiEndpoints';
import { CHARACTERS, ENHANCED_CHARACTERS, EXPANDED_EMOTIONS } from "./DemoConstants";
import CountrySelector from "./CountrySelector";
import MethodToggle from "./MethodToggle";
import AccentToggle from "./AccentToggle";
import { SOUNDWAVE_CARDS } from "./DemoConstants";   // add near top of file
import refreshMap from '../../Assets/Images/refresh-map.png'; // Your map

const getAuthHeaders = () => {
    try {
        const token = JSON.parse(localStorage.getItem("userInfo") || "{}")?.token;
        return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
        return {};
    }
};

const Demo2025 = () => {
    // Toggle between old and new methods
    const [useNewMethod, setUseNewMethod] = useState(false);
    const [useAccentMap, setUseAccentMap] = useState(false);

    const [activeCharacterIndex, setActiveCharacterIndex] = useState(0);
    const [selectedEmotion, setSelectedEmotion] = useState(useNewMethod ? "Neutral" : "Happy");
    const [selectedCountry, setSelectedCountry] = useState("American-Accent-v1");
    const [inputText, setInputText] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // New state to track available emotions and countries for the enhanced method
    const [availableEmotions, setAvailableEmotions] = useState([]);
    const [availableCountries, setAvailableCountries] = useState([]);
    const [attributions, setAttributions] = useState([]);

    // Performance metrics state variables
    const [cost, setCost] = useState(null);
    const [savings, setSavings] = useState(null);

    // Audio element reference
    const audioRef = useRef(null);
    const inputRef = useRef(null);

    // Store the last successful request parameters for comparison
    const lastRequestRef = useRef({
        text: "",
        voiceId: "",
        voiceProvider: "",
        emotion: "",
        accent: ""
    });

    // Store the last generated audio URL
    const lastAudioUrlRef = useRef("");

    // Get the appropriate character list based on method
    const characterList = useNewMethod ? ENHANCED_CHARACTERS : CHARACTERS;

    // Get the current selected character
    const currentCharacter = characterList[activeCharacterIndex];

    // Handle method toggle
    const handleMethodToggle = () => {
        const newMethodValue = !useNewMethod;
        setUseNewMethod(newMethodValue);
        setError(null);

        if (newMethodValue) {
            // Switching to v1 mode
            updateAvailableOptions(characterList[activeCharacterIndex]);
            // Set Neutral for v1 mode
            setSelectedEmotion("Neutral");
        } else {
            // Switching to v2 mode - set a different default emotion
            setSelectedEmotion("Happy");  // Use a different emotion for v2 mode
            // Set default country
            setSelectedCountry("American-Accent-v1");
        }
    };

    // Update available emotions and countries based on the current character
    const updateAvailableOptions = useCallback((character) => {
        if (!character) return;

        if (useNewMethod) {
            // For emotions, get the keys from the emotions object
            const emotionIds = character.emotions ? Object.keys(character.emotions) : [];
            setAvailableEmotions(emotionIds);

            // For countries/accents, get the keys from the accents object
            const countryIds = character.accents ? Object.keys(character.accents) : [];
            setAvailableCountries(countryIds);

            // Reset selections if they're not available for this character
            if (emotionIds.length > 0 && !emotionIds.includes(selectedEmotion)) {
                setSelectedEmotion(emotionIds[0]);
            }

            if (countryIds.length > 0 && selectedCountry && !countryIds.includes(selectedCountry)) {
                setSelectedCountry(countryIds[0]);
            } else if (countryIds.length > 0 && !selectedCountry) {
                setSelectedCountry(countryIds[0]);
            }
        } else {
            // In standard mode, all emotions and countries are available
            setAvailableEmotions(EXPANDED_EMOTIONS.map(emotion => emotion.id));
            // You might want to set available countries from your constants as well
            setAvailableCountries([]);  // Will use all countries from CountrySelector
        }
    }, [selectedCountry, selectedEmotion, useNewMethod]);

    // Map characters to images for the carousel
    const images = characterList.map((_, index) => {
        const cardIndex = index % SOUNDWAVE_CARDS.length;
        return SOUNDWAVE_CARDS[cardIndex];
    });

    // Function to check if the current request is the same as the last one
    const isSameRequest = useCallback((currentRequest) => {
        const lastRequest = lastRequestRef.current;
        return (
            currentRequest.text === lastRequest.text &&
            currentRequest.voiceId === lastRequest.voiceId &&
            currentRequest.voiceProvider === lastRequest.voiceProvider &&
            currentRequest.emotion === lastRequest.emotion &&
            currentRequest.accent === lastRequest.accent
        );
    }, []);

    // Handle country selection with auto-regeneration
    const handleCountrySelect = (countryId) => {
        console.log("Country selected:", countryId);

        setError(null);

        // Only regenerate if the country actually changed
        if (countryId !== selectedCountry) {
            setSelectedCountry(countryId);

            // For v1 mode (when useNewMethod is true), do nothing with audio
            // For v2 mode (when useNewMethod is false), regenerate audio
            if (inputText.trim() && !useNewMethod) {
                // Use setTimeout to ensure state update completes first
                setTimeout(() => generateAudio(selectedEmotion, countryId), 0);
            }
            // No audio replay for v1 mode when changing country
        } else {
            // If it's the same country and not in v1 mode, replay existing audio
            if (!useNewMethod) {
                replayAudioIfAvailable();
            }
        }
    };

    // Function to replay existing audio if available
    const replayAudioIfAvailable = useCallback(() => {
        if (lastAudioUrlRef.current && audioRef.current) {
            console.log("No change detected, replaying existing audio");
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch((err) => {
                console.warn("Error replaying audio:", err);
            });
        }
    }, []);

    // Create request data based on selected method
    const createRequestData = useCallback((emotion, country) => {
        const selectedCharacter = characterList[activeCharacterIndex];

        if (useNewMethod) {
            // For new method, get the specific voice ID based on the selected emotion
            const emotionVoiceId = selectedCharacter.emotions[emotion] || selectedCharacter.id;

            return {
                text: inputText,
                voiceId: emotionVoiceId,  // Use emotion-specific voice ID
                voiceProvider: selectedCharacter.provider,
                emotion: emotion,
                accent: country,
                // Pass any additional accent-specific settings if needed
                accentSettings: country && selectedCharacter.accents[country] ?
                    selectedCharacter.accents[country] : null
            };
        } else {
            // Use the standard method (original implementation)
            return {
                text: inputText,
                voiceId: selectedCharacter.id,
                voiceProvider: selectedCharacter.provider,
                emotion: emotion,
                accent: country
            };
        }
    }, [activeCharacterIndex, characterList, inputText, useNewMethod]);

    // Generate audio with specific emotion and country
    const generateAudio = useCallback(async (emotion, country, forceRegenerate = false) => {
        if (!inputText.trim()) {
            setError("Please enter some text");
            return;
        }
        setError(null);

        const currentRequest = createRequestData(emotion, country);

        // Check if this is the same as the last request
        if (!forceRegenerate && isSameRequest(currentRequest) && lastAudioUrlRef.current) {
            replayAudioIfAvailable();
            return;
        }

        // If we get here, we need to make a new API call
        setIsLoading(true);

        try {
            // Reset any existing audio to avoid "interrupted by new load request" errors
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = "";
                audioRef.current.load(); // ensures the old audio is fully reset
            }

            // Choose the appropriate API endpoint based on the method
            const apiEndpoint = useNewMethod ? GENERATE_ENHANCED_TTS_AUDIO : GENERATE_TTS_AUDIO;

            console.log(`Sending request to ${useNewMethod ? 'enhanced' : 'standard'} API:`, currentRequest);
            // const response = await axios.post(apiEndpoint, currentRequest);
            const response = await axios.post(
                apiEndpoint,
                currentRequest,
                { headers: getAuthHeaders() }
            );

            if (response.data.success && audioRef.current) {
                // Store this request and its audio URL for future reference
                lastRequestRef.current = { ...currentRequest };
                lastAudioUrlRef.current = response.data.audioUrl;

                // Set the audio source but don't play immediately
                audioRef.current.src = response.data.audioUrl;

                // Listen for the 'canplaythrough' event instead of playing immediately
                audioRef.current.addEventListener('canplaythrough', function onCanPlay() {
                    // Remove the event listener to avoid duplicate plays
                    audioRef.current.removeEventListener('canplaythrough', onCanPlay);
                    // Play the audio now that it's fully loaded
                    audioRef.current.play().catch((err) => {
                        console.warn("Auto-play error:", err);
                    });
                }, { once: true }); // Use once:true to ensure it only fires once

                // Load the audio
                audioRef.current.load();

                // Update performance metrics from the API response
                if (response.data.cost) {
                    setCost(parseFloat(response.data.cost.amount).toFixed(5));
                }


                if (response.data.savings) {
                    setSavings(response.data.savings);
                }

                try {

                    const selectedCharacter = characterList[activeCharacterIndex];
                    const voiceId = selectedCharacter.id;

                    const attributionResponse = await axios.post(ATTRIBUTION, {
                        character: voiceId,
                        accent: currentRequest.accent
                    }, { headers: getAuthHeaders() });

                    if (attributionResponse.data.success) {
                        setAttributions([attributionResponse.data.attribution]);
                    } else {
                        // Silently clear attributions if the call failed
                        setAttributions([]);
                    }
                } catch (attributionError) {
                    // Silently handle attribution errors - just log and clear attributions
                    console.log("Attribution API failed:", attributionError.message);
                    setAttributions([]);
                }
            } else {
                throw new Error(response.data.message || "Failed to generate audio");
            }
        } catch (err) {
            console.error("Error generating audio:", err);
            setError(err.message || "An error occurred while generating audio");
        } finally {
            setIsLoading(false);
        }
    }, [
        activeCharacterIndex,
        audioRef,
        characterList,
        createRequestData,
        inputText,
        isSameRequest,
        replayAudioIfAvailable,
        setAttributions,
        setCost,
        setSavings,
        useNewMethod,
    ]);

    // Handle emotion selection with auto-regeneration
    const handleEmotionSelect = useCallback((emotion) => {
        console.log("Emotion selected:", emotion);

        // In enhanced mode, check if the emotion is available for this character
        if (useNewMethod && !availableEmotions.includes(emotion)) {
            setError(`Emotion "${emotion}" is not available for ${currentCharacter.name}`);
            return;
        }

        // Clear any errors
        setError(null);

        // Only regenerate if the emotion actually changed
        if (emotion !== selectedEmotion) {
            setSelectedEmotion(emotion);

            // Check if we have text to generate with
            if (inputText.trim()) {
                // Use setTimeout to ensure state update completes first
                setTimeout(() => generateAudio(emotion, selectedCountry), 0);
            }
        } else {
            // If it's the same emotion, just replay existing audio
            replayAudioIfAvailable();
        }
    }, [
        availableEmotions,
        currentCharacter,
        generateAudio,
        inputText,
        replayAudioIfAvailable,
        selectedCountry,
        selectedEmotion,
        useNewMethod,
    ]);

    // Handle the generate button click
    const handleGenerateAudio = (forceRegenerate = false) => {
        const accentToUse = useAccentMap ? selectedCountry : "American-Accent-v1";
        generateAudio(selectedEmotion, accentToUse, forceRegenerate);
    };

    // When the text changes, clear the last request reference
    useEffect(() => {
        // This makes sure we generate new audio when text changes
        lastRequestRef.current.text = "";
    }, [inputText]);

    // Reset active character index if it's out of bounds after toggling method
    useEffect(() => {
        const newCharacterList = useNewMethod ? ENHANCED_CHARACTERS : CHARACTERS;
        if (activeCharacterIndex >= newCharacterList.length) {
            setActiveCharacterIndex(0);
        }

        // Set appropriate default emotion when switching modes
        if (useNewMethod && selectedEmotion === "Happy") {
            setSelectedEmotion("Neutral");
        } else if (!useNewMethod && selectedEmotion === "Neutral") {
            setSelectedEmotion("Happy");
        }
    }, [useNewMethod, activeCharacterIndex, selectedEmotion]);

    // Update available options when character changes or method changes
    useEffect(() => {
        // When character changes, clear the last request reference
        lastRequestRef.current.voiceId = "";

        // Update available options based on the new character
        updateAvailableOptions(characterList[activeCharacterIndex]);
    }, [activeCharacterIndex, characterList, updateAvailableOptions, useNewMethod]);

    const handleCountryAccentChange = (countryId) => {
        // Find a character that supports this country/accent
        if (useNewMethod) {
            const characterWithAccent = ENHANCED_CHARACTERS.findIndex(character =>
                character.accents && Object.keys(character.accents).includes(countryId)
            );

            if (characterWithAccent !== -1) {
                setActiveCharacterIndex(characterWithAccent);
                // Just update the country without auto-generating audio in v1 mode
                setSelectedCountry(countryId);
            }
        } else {
            // For v2 mode, use the regular handleCountrySelect function
            handleCountrySelect(countryId);
        }
    };

    useEffect(() => {
        const handleKeyDown = (event) => {
            const isInputFocused = document.activeElement === inputRef.current;

            if (isInputFocused) return;

            if (event.key.toLowerCase() === 'e') {
                event.preventDefault();

                const emotions = useNewMethod ? availableEmotions : EXPANDED_EMOTIONS.map(e => e.id);
                const currentIndex = emotions.indexOf(selectedEmotion);
                if (currentIndex === -1) return;

                const nextIndex = event.shiftKey
                    ? (currentIndex - 1 + emotions.length) % emotions.length
                    : (currentIndex + 1) % emotions.length;

                handleEmotionSelect(emotions[nextIndex]);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [availableEmotions, handleEmotionSelect, selectedEmotion, useNewMethod]);


    return (
        <div className={classes["demo-root"]}>
            <div className={classes["demo-container"]}>
                {/* Header */}
                <h1 className={classes["header"]}>An InCantor AI Private Preview</h1>
                <hr className={classes["divider"]} />
                <h2 className={classes["subheader"]}>Text-to-Performance</h2>

                {/* Main Content */}
                <div className={classes["main-content"]}>
                    {/* Emotion Wheel (Left) */}
                    <div className={classes["emotion-selector"]}>
                        <EmotionWheel
                            onEmotionSelect={handleEmotionSelect}
                            selectedEmotion={selectedEmotion}
                            // Pass filtered emotions when in enhanced mode
                            availableEmotions={useNewMethod ? availableEmotions : null}
                        />
                    </div>

                    {/* Carousel (Center) */}
                    <div className={classes["carousel-wrapper"]}>
                        <GradientCarousel
                            images={images}
                            characters={characterList}
                            activeIndex={activeCharacterIndex}
                            setActiveIndex={setActiveCharacterIndex}
                            isLoading={isLoading}
                        />
                    </div>

                    {/* Country Selector (Right) */}
                    <div
                        className={`${classes["country-selector"]} ${!useAccentMap ? classes["refresh-mode"] : ""}`}
                    >                        {useAccentMap ? (
                        <CountrySelector
                            onCountrySelect={handleCountrySelect}
                            selectedCountry={selectedCountry}
                            availableCountries={useNewMethod ? availableCountries : null}
                            useNewMethod={useNewMethod}
                            onCharacterChange={handleCountryAccentChange}
                        />
                    ) : (
                        <img
                            src={refreshMap}
                            alt="Regenerate"
                            style={{
                                width: '128px',
                                height: '128px',
                                cursor: 'pointer',
                                opacity: 0.7,
                                transition: 'transform 0.2s ease',
                            }}
                            onClick={() => handleGenerateAudio(true)}
                            onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.1)')}
                            onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
                        />

                    )}
                    </div>
                </div>

                {/* Text Input & Submit Button */}
                <TextInputArea
                    inputText={inputText}
                    setInputText={setInputText}
                    isLoading={isLoading}
                    onGenerateAudio={() => handleGenerateAudio(false)}
                    onInsertEmoji={(emoji) => {
                        if (!isLoading) {
                            setInputText(inputText + emoji);
                        }
                    }}
                    useNewMethod={useNewMethod}
                    ref={inputRef}
                />

                {/* Error Message */}
                {error && (
                    <div className={classes["error-message"]}>
                        {error}
                    </div>
                )}

                {/* Performance Metrics and Attributions */}
                <div className={classes["performance-metrics-container"]}>
                    <div className={classes["metrics-col"]}>
                        {/* {speedMs !== null && (
                            <div className={classes["metric"]}>Speed: {speedMs}ms</div>
                        )} */}

                        {attributions.length > 0 &&
                            <div className={classes["metrics-col"]}>Attribution:</div>
                        }
                        {attributions.map((attribution, index) => (
                            <div key={index} className={classes["metric"]}>
                                {attribution} (DubFrob, Inc.)
                            </div>
                        ))}
                    </div>

                    <div className={classes["metrics-col"]}>
                        {cost !== null && (
                            <div className={classes["metric"]}>Est. Cost: ${cost} (TBD)</div>
                        )}
                        {savings !== null && (
                            <div className={classes["metric"]}>Est. Savings: {savings}%</div>
                        )}
                    </div>
                </div>

                {/* Performance Metrics */}
                {/* {(speedMs !== null || cost !== null || savings !== null) && (
                    <div className={classes["performance-metrics-container"]}>
                        <div className={classes["performance-metrics-row"]}>
                            {speedMs !== null && (
                                <span className={classes["metric"]}>Speed: {speedMs}ms</span>
                            )}
                            {cost !== null && (
                                <span className={classes["metric"]}>Cost: ${cost} (TBD)</span>
                            )}
                        </div>
                        {savings !== null && (
                            <div className={classes["savings-row"]}>
                                <span className={classes["metric"]}>Savings: {savings}%</span>
                            </div>
                        )}
                    </div>
                )} */}

                {/* Method Toggle in bottom right */}
                <div
                    style={{
                        position: 'absolute',
                        bottom: '15px',
                        right: '15px',
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '30px',
                        padding: '6px 12px',
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        borderRadius: '16px',
                        zIndex: 1000,
                        transform: 'scale(0.85)',
                        opacity: '20%',
                    }}
                >
                    <MethodToggle
                        useNewMethod={useNewMethod}
                        onToggle={handleMethodToggle}
                    />
                    <AccentToggle
                        useAccentMap={useAccentMap}
                        onToggle={() => setUseAccentMap(!useAccentMap)}
                    />
                </div>


                {/* Logo in bottom left */}
                <div className={classes["logo-container"]}>
                    <img src="/logo-incantor-av-dark.png" alt="InCantor Logo" className={classes["logo-image"]} />
                </div>
            </div>

            {/* Hidden audio element */}
            <audio ref={audioRef} style={{ display: "none" }} />
        </div>
    );
};

export default Demo2025;